import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, ArrowLeft, Upload, AlertCircle, X, Plus, Star, ChevronUp, ChevronDown } from 'lucide-react';
import Button from '../components/ui/Button';
import { useQuery } from '@apollo/client';
import { GET_CATEGORIES } from '../graphql/queries/categories';
import { useMutation } from '@apollo/client';
import { CREATE_TOOL } from '../graphql/mutations/createTool';
import ToolAvailabilityCalendar, { AvailabilityPeriod } from '../components/features/ToolAvailabilityCalendar';
import { ACCOUNT_SETTINGS } from '../graphql/queries/accountSettings';
import { useAuth } from '../context/AuthContext';
import { TOOL_CONDITION_OPTIONS, ToolCondition } from '../lib/toolCondition';
import { MAX_IMAGE_UPLOAD_BYTES, MAX_TOTAL_IMAGE_UPLOAD_BYTES, prepareImageFiles } from '../lib/imageUpload';

type ToolFormData = {
  name: string;
  description: string;
  pricePerDay: string;
  address: string;
  city: string;
  postalCode: string;
  categories: string[];
  primaryImageUrl: string;
  imageFiles: File[];
  primaryImageIndex: number;
  brandName: string;
  model: string;
  powerDescription: string;
  condition: ToolCondition | '';
  weightGrams: string;
  ageMonths: string;
  maxAvailableCount: string;
  whatsIncluded: string[];
  usageInstructions: string;
  availabilityPeriods: AvailabilityPeriod[];
};

const AddTool: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<ToolFormData>({
    name: '',
    description: '',
    pricePerDay: '',
    address: '',
    city: '',
    postalCode: '',
    categories: [] as string[],
    primaryImageUrl: '',
    imageFiles: [],
    primaryImageIndex: 0,
    brandName: '',
    model: '',
    powerDescription: '',
    condition: '',
    weightGrams: '',
    ageMonths: '',
    maxAvailableCount: '1',
    whatsIncluded: [''],
    usageInstructions: '',
    availabilityPeriods: [
      {
        startDate: new Date().toISOString().split('T')[0],
        endDate: undefined,
        available: true,
      },
    ],
  });

  const addImages = async (files: FileList) => {
    const { files: preparedFiles, errors } = await prepareImageFiles(files);

    if (errors.length > 0) {
      setError(errors[0]);
    }

    if (preparedFiles.length === 0) {
      return;
    }

    const currentTotalBytes = formData.imageFiles.reduce((sum, file) => sum + file.size, 0);
    const acceptedFiles: File[] = [];
    let nextTotalBytes = currentTotalBytes;

    for (const file of preparedFiles) {
      if (nextTotalBytes + file.size <= MAX_TOTAL_IMAGE_UPLOAD_BYTES) {
        acceptedFiles.push(file);
        nextTotalBytes += file.size;
      }
    }

    if (acceptedFiles.length === 0) {
      setError(`Samlet upload er for stor. Maks ${Math.round(MAX_TOTAL_IMAGE_UPLOAD_BYTES / (1024 * 1024))}MB for alle billeder.`);
      return;
    }

    if (acceptedFiles.length < preparedFiles.length) {
      setError(`Nogle billeder blev ikke tilføjet pga. samlet max på ${Math.round(MAX_TOTAL_IMAGE_UPLOAD_BYTES / (1024 * 1024))}MB.`);
    }

    setFormData(prev => ({
      ...prev,
      imageFiles: [...prev.imageFiles, ...acceptedFiles]
    }));
  };

  const removeImageAtIndex = (index: number) => {
    setFormData(prev => {
      const newFiles = prev.imageFiles.filter((_, i) => i !== index);
      let newPrimaryIndex = prev.primaryImageIndex;
      
      // Adjust primary index if needed
      if (index === prev.primaryImageIndex && newFiles.length > 0) {
        newPrimaryIndex = 0;
      } else if (index < prev.primaryImageIndex) {
        newPrimaryIndex = prev.primaryImageIndex - 1;
      } else if (prev.primaryImageIndex >= newFiles.length && newFiles.length > 0) {
        newPrimaryIndex = newFiles.length - 1;
      }
      
      return {
        ...prev,
        imageFiles: newFiles,
        primaryImageIndex: newPrimaryIndex
      };
    });
  };

  const moveImage = (index: number, direction: 'up' | 'down') => {
    setFormData(prev => {
      const newFiles = [...prev.imageFiles];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      
      if (newIndex < 0 || newIndex >= newFiles.length) return prev;
      
      // Swap files
      [newFiles[index], newFiles[newIndex]] = [newFiles[newIndex], newFiles[index]];
      
      // Update primary index if needed
      let newPrimaryIndex = prev.primaryImageIndex;
      if (prev.primaryImageIndex === index) {
        newPrimaryIndex = newIndex;
      } else if (prev.primaryImageIndex === newIndex) {
        newPrimaryIndex = index;
      }
      
      return {
        ...prev,
        imageFiles: newFiles,
        primaryImageIndex: newPrimaryIndex
      };
    });
  };

  const setPrimaryImage = (index: number) => {
    setFormData(prev => ({ ...prev, primaryImageIndex: index }));
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      void addImages(files);
    }
  };

  const [categories, setCategories] = useState<any[]>([]);
  const { data: catData } = useQuery(GET_CATEGORIES);
  const { data: accountData } = useQuery(ACCOUNT_SETTINGS, {
    skip: !user,
    fetchPolicy: 'network-only',
  });
  const [createTool] = useMutation(CREATE_TOOL);

  useEffect(() => {
    if (catData) {
      setCategories(catData.categories);
    }
  }, [catData]);

  useEffect(() => {
    const currentUser = accountData?.currentUser;
    if (!currentUser) return;

    setFormData((prev) => ({
      ...prev,
      address: prev.address || currentUser.address || '',
      postalCode: prev.postalCode || currentUser.postalCode || '',
      city: prev.city || currentUser.city || '',
    }));
  }, [accountData]);

  const encodeFileToBase64 = (file: File) =>
    new Promise<{ base64: string; filename: string; contentType: string }>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve({
          base64,
          filename: file.name,
          contentType: file.type,
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleCategoryToggle = (category: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleIncludedItemChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      whatsIncluded: prev.whatsIncluded.map((item, i) => 
        i === index ? value : item
      )
    }));
  };

  const addIncludedItem = () => {
    setFormData(prev => ({
      ...prev,
      whatsIncluded: [...prev.whatsIncluded, '']
    }));
  };

  const removeIncludedItem = (index: number) => {
    if (formData.whatsIncluded.length > 1) {
      setFormData(prev => ({
        ...prev,
        whatsIncluded: prev.whatsIncluded.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!formData.name || !formData.description || !formData.pricePerDay || !formData.address || !formData.city || !formData.postalCode) {
        throw new Error('Udfyld venligst alle påkrævede felter');
      }

      if (formData.categories.length === 0) {
        throw new Error('Vælg venligst mindst én kategori');
      }

      const price = parseFloat(formData.pricePerDay);
      if (isNaN(price) || price <= 0) {
        throw new Error('Indtast venligst en gyldig pris');
      }

      // Encode alle billeder
      const imagesPayload = [];
      if (formData.imageFiles.length > 0) {
        for (let i = 0; i < formData.imageFiles.length; i++) {
          const imageData = await encodeFileToBase64(formData.imageFiles[i]);
          imagesPayload.push({
            image: imageData,
            position: i + 1,
            primary: i === formData.primaryImageIndex
          });
        }
      }

      await createTool({
        variables: {
          input: {
            name: formData.name,
            description: formData.description,
            address: formData.address,
            postalCode: formData.postalCode,
            city: formData.city,
            pricePerDayCents: parseFloat(formData.pricePerDay) * 100,
            categoryIds: formData.categories,
            images: imagesPayload,
            brandName: formData.brandName,
            model: formData.model,
            powerDescription: formData.powerDescription,
            condition: formData.condition || null,
            weightGrams: formData.weightGrams ? parseInt(formData.weightGrams) : null,
            ageMonths: formData.ageMonths ? parseInt(formData.ageMonths) : null,
            usageInstructions: formData.usageInstructions,
            maxAvailableCount: formData.maxAvailableCount ? parseInt(formData.maxAvailableCount) : null,
            includedItems: formData.whatsIncluded.filter(item => item.trim() !== ''),
            toolAvailabilityPeriods: formData.availabilityPeriods.map(period => ({
              startDate: period.startDate,
              endDate: period.endDate || null,
              available: period.available,
            })),
          },
        },
      });

      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke tilføje værktøj');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate(-1)} className="flex items-center text-green-600 mb-8 hover:text-green-700">
            <ArrowLeft className="h-5 w-5 mr-2" />
            <span>Tilbage til dashboard</span>
          </button>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Tilføj dit værktøj</h1>
              <p className="mt-2 text-gray-600">
                Del dit værktøj med fællesskabet og tjen penge
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Tool Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Værktøjs navn <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="F.eks., DeWalt skruemaskine"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Beskrivelse <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="Beskriv dit værktøj, dets stand og eventuelle særlige funktioner..."
                  required
                />
              </div>

              {/* Price and Location */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                    Pris per dag <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm" style={{display: 'flex', alignItems: 'center'}}>
                    <input
                      type="number"
                      id="price"
                      min="0"
                      step="0.01"
                      value={formData.pricePerDay}
                      onChange={(e) => setFormData(prev => ({ ...prev, pricePerDay: e.target.value }))}
                      className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                      placeholder="0,00"
                      required
                      style={{ textAlign: "right" }}
                    />
                    &nbsp;
                    <div>kr.</div>
                  </div>
                </div>
              </div>

              {/* Adresse */}
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Adresse</h2>
                  <p className="text-sm text-gray-500">
                    Værktøjets placering er skjult for lejer indtil bookningen er accepteret
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                      Vejnavn og husnummer <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                      placeholder="F.eks. Vestergade 12"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
                        Postnummer <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="postalCode"
                        value={formData.postalCode}
                        onChange={(e) => setFormData(prev => ({ ...prev, postalCode: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                        placeholder="F.eks. 8000"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                        By <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                        placeholder="F.eks. Aarhus C"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

    
              {/* Categories */}
              <div>
                 <h2 className="text-xl font-semibold text-gray-900 mb-6">Kategorier <span className="text-red-500">*</span></h2>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleCategoryToggle(category.id)}
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                        formData.categories.includes(category.id)
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Specifikationer */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Specifikationer</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Tilføj tekniske detaljer om dit værktøj
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="brandName" className="block text-sm font-medium text-gray-700 mb-1">
                      Mærke
                    </label>
                    <input
                      type="text"
                      id="brandName"
                      placeholder="F.eks. DeWalt, Bosch"
                      value={formData.brandName}
                      onChange={(e) => setFormData(prev => ({ ...prev, brandName: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-1">
                      Model
                    </label>
                    <input
                      type="text"
                      id="model"
                      placeholder="F.eks. DCD777C2"
                      value={formData.model}
                      onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="powerDescription" className="block text-sm font-medium text-gray-700 mb-1">
                      Effekt
                    </label>
                    <input
                      type="text"
                      id="powerDescription"
                      placeholder="F.eks. 20V, 1500W"
                      value={formData.powerDescription}
                      onChange={(e) => setFormData(prev => ({ ...prev, powerDescription: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="condition" className="block text-sm font-medium text-gray-700 mb-1">
                      Stand
                    </label>
                    <select
                      id="condition"
                      value={formData.condition}
                      onChange={(e) => setFormData(prev => ({ ...prev, condition: e.target.value as ToolCondition | '' }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    >
                      <option value="">Vælg stand</option>
                      {TOOL_CONDITION_OPTIONS.map((conditionOption) => (
                        <option key={conditionOption.value} value={conditionOption.value}>
                          {conditionOption.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="weightGrams" className="block text-sm font-medium text-gray-700 mb-1">
                      Vægt (gram)
                    </label>
                    <input
                      type="number"
                      id="weightGrams"
                      placeholder="F.eks. 1500"
                      value={formData.weightGrams}
                      onChange={(e) => setFormData(prev => ({ ...prev, weightGrams: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="ageMonths" className="block text-sm font-medium text-gray-700 mb-1">
                      Alder (måneder)
                    </label>
                    <input
                      type="number"
                      id="ageMonths"
                      placeholder="F.eks. 24"
                      value={formData.ageMonths}
                      onChange={(e) => setFormData(prev => ({ ...prev, ageMonths: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="maxAvailableCount" className="block text-sm font-medium text-gray-700 mb-1">
                      Maks. antal tilgængelige
                    </label>
                    <input
                      type="number"
                      id="maxAvailableCount"
                      min="1"
                      placeholder="F.eks. 1"
                      value={formData.maxAvailableCount}
                      onChange={(e) => setFormData(prev => ({ ...prev, maxAvailableCount: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Hvad er inkluderet */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Hvad er inkluderet</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Angiv alle tilbehør der følger med værktøjet (kuffert, batteri, bor osv.)
                </p>
                
                <div className="space-y-3">
                  {formData.whatsIncluded.map((item, index) => (
                    <div key={index} className="flex gap-3">
                      <div className="flex-1">
                        <input
                          type="text"
                          placeholder="F.eks. Kuffert, Ekstra batteri, Borsæt"
                          value={item}
                          onChange={(e) => handleIncludedItemChange(index, e.target.value)}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeIncludedItem(index)}
                        className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500"
                        disabled={formData.whatsIncluded.length === 1}
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
                
                <button
                  type="button"
                  onClick={addIncludedItem}
                  className="mt-3 inline-flex items-center text-sm text-green-600 hover:text-green-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Tilføj element
                </button>
              </div>

              {/* Brugsanvisning */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Brugsanvisning</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Giv hjælpsomme instruktioner til lånere om hvordan de sikkert bruger dit værktøj
                </p>
                
                <textarea
                  rows={6}
                  value={formData.usageInstructions}
                  onChange={(e) => setFormData(prev => ({ ...prev, usageInstructions: e.target.value || '' }))}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                  placeholder="Beskriv trin-for-trin instruktioner, sikkerhedstips og særlige hensyn ved brug af dit værktøj..."
                />
              </div>

              {/* Tilgængelighed */}
              <div>
                <ToolAvailabilityCalendar
                  periods={formData.availabilityPeriods}
                  onChange={(periods) => setFormData(prev => ({ ...prev, availabilityPeriods: periods }))}
                />
              </div>

              {/* Image Upload */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Billeder <span className="text-red-500">*</span></h2>
                <p className="text-sm text-gray-600 mb-4">
                  Tilføj et eller flere billeder. Det første billede vil være dit hovedbillede.
                </p>
                
                {/* Existing Images */}
                {formData.imageFiles.length > 0 && (
                  <div className="mb-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {formData.imageFiles.map((file, index) => (
                      <div key={index} className="relative border-2 rounded-lg p-2 bg-white">
                        {/* Primary badge */}
                        {index === formData.primaryImageIndex && (
                          <div className="absolute top-1 left-1 bg-green-600 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1 z-10">
                            <Star className="h-3 w-3 fill-current" />
                            Primær
                          </div>
                        )}
                        
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => setPreviewImageUrl(URL.createObjectURL(file))}
                        />
                        
                        <div className="mt-2 flex items-center justify-between gap-2">
                          {/* Reorder buttons */}
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => moveImage(index, 'up')}
                              disabled={index === 0}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Flyt op"
                            >
                              <ChevronUp className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveImage(index, 'down')}
                              disabled={index === formData.imageFiles.length - 1}
                              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Flyt ned"
                            >
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </div>
                          
                          {/* Set as primary */}
                          {index !== formData.primaryImageIndex && (
                            <button
                              type="button"
                              onClick={() => setPrimaryImage(index)}
                              className="p-1 text-gray-400 hover:text-green-600"
                              title="Sæt som primær"
                            >
                              <Star className="h-4 w-4" />
                            </button>
                          )}
                          
                          {/* Remove button */}
                          <button
                            type="button"
                            onClick={() => removeImageAtIndex(index)}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Fjern billede"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload area */}
                <div 
                  className={`flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
                    isDragging 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-300'
                  }`}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="space-y-1 text-center">
                    <Upload className={`mx-auto h-12 w-12 ${
                      isDragging ? 'text-green-500' : 'text-gray-400'
                    }`} />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="image-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-green-600 hover:text-green-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500"
                      >
                        <span>Tilføj billeder</span>
                        <input
                          id="image-upload"
                          name="image-upload"
                          type="file"
                          multiple
                          className="sr-only"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files && e.target.files.length > 0) {
                              void addImages(e.target.files);
                            }
                          }}
                        />
                      </label>
                      <p className="pl-1">eller træk og slip</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, WEBP, GIF. Komprimeres automatisk. Maks {Math.round(MAX_IMAGE_UPLOAD_BYTES / (1024 * 1024))}MB pr. billede.
                    </p>
                  </div>
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                >
                  Annullér
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={isLoading}
                  icon={<PlusCircle className="h-4 w-4 mr-2" />}
                >
                  Tilføj værktøj
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImageUrl && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImageUrl(null)}
        >
          <button
            onClick={() => setPreviewImageUrl(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 p-2 bg-black bg-opacity-50 rounded-full"
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={previewImageUrl}
            alt="Preview"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default AddTool;